<template>
  <v-layout>
    <v-dialog :modelValue="dialog" @click:outside="$emit('updateDialog', false)" @keydown.esc="$emit('updateDialog', false)" max-width="900px" transition="dialog-bottom-transition">
      <template v-slot:activator="{ props }">
        <v-btn flat class="mb-3 btn-second" v-bind="props" @click="$emit('updateDialog', true)">{{ button_name }}</v-btn>
      </template>

      <v-card>
        <v-form @submit.prevent ref="form" v-model="valid" v-on:submit.prevent="createItem">
          <v-card-title>
            <span class="headline">{{ modal_title }}</span>
          </v-card-title>

          <v-card-text>
            <v-row align="center" justify="center" class="mb-4 mt-4">
              <v-col md="10" class="elevation-1 pt-4 pb-4" style="background: #f5f5f5">
                <a-steps :current="step">
                  <a-step v-for="s in steps" :key="s.title" :title="s.title" />
                </a-steps>
              </v-col>
            </v-row>

            <div class="steps-content">
              <!--step 0 - Datos-->
              <v-row align="start" justify="center" v-if="step == 0" transition="fade-transition">
                <v-col md="10" class="mt-4 pr-0 pl-0">
                  <v-form ref="form_step0" v-model="validStep0">
                    <div class="px-2 py-1 mb-3 bg-subtitle-section">
                      <p class="text-subtitle-1 mb-0"><b>Datos del conteo</b></p>
                    </div>
                    <p>El conteo nace en <b>borrador</b>: nada se bloquea hasta que lo emitas.</p>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-combobox
                          label="Almacén*"
                          v-model="item.almacen"
                          :items="almacenes"
                          item-title="name"
                          item-value="id"
                          clearable
                          :disabled="disabled"
                          :rules="[rules.required]"
                          hint="Solo se van a poder elegir ubicaciones de este almacén"
                          persistent-hint
                          @update:modelValue="onAlmacenChange"
                        ></v-combobox>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-combobox label="Tipo*" v-model="item.type" :items="types" item-title="name" item-value="id" clearable :disabled="disabled || item.id > 0" :rules="[rules.required]"></v-combobox>
                        <div v-if="es_alta_inicial" class="text-caption mt-1 text-medium-emphasis">
                          El alta inicial arranca con el teórico en <b>cero</b>: todo lo que cargue el operario va a ser hallazgo.
                        </div>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-combobox
                          label="Cliente"
                          v-model="item.client"
                          :items="clients"
                          item-title="name"
                          item-value="id"
                          clearable
                          :disabled="disabled"
                          hint="Acota qué ubicaciones entran, no qué se cuenta adentro"
                          persistent-hint
                          @update:modelValue="onClientChange"
                        ></v-combobox>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field label="Fecha planificada" v-model="item.scheduled_date" type="date" :disabled="disabled" clearable></v-text-field>
                      </v-col>

                      <v-col cols="12">
                        <v-textarea label="Observaciones" v-model="item.observations" :disabled="disabled" rows="3" auto-grow></v-textarea>
                      </v-col>
                    </v-row>

                    <!--
                      Aviso deliberado: es la consecuencia menos obvia del conteo y la que
                      genera llamados al soporte. La ubicacion no tiene dueño, asi que el
                      bloqueo y el congelado alcanzan a todos los clientes que tengan stock ahi.
                    -->
                    <v-alert v-if="item.client != null" type="info" variant="tonal" density="compact" class="mt-2 mb-4">
                      El conteo se cuenta por ubicación: se va a congelar y contar <b>todo</b> el contenido de cada ubicación elegida, sea del cliente que sea. El cliente solo acota cuáles entran.
                    </v-alert>
                  </v-form>

                  <v-btn class="float-right btn-second " v-if="step < steps.length - 1" @click="next">Siguiente</v-btn>
                </v-col>
              </v-row>

              <!--step 1 - Ubicaciones-->
              <v-row align="start" justify="center" v-if="step == 1" transition="fade-transition">
                <v-col md="10" class="mt-4 pr-0 pl-0">
                  <div class="px-2 py-1 mb-3 bg-subtitle-section">
                    <p class="text-subtitle-1 mb-0"><b>Ubicaciones a contar</b></p>
                  </div>
                  <p>Elegí las ubicaciones que va a recorrer el operario. Se guardan al confirmar, junto con el resto del conteo.</p>

                  <!--
                    Atajo para acotar el trabajo: el cliente NO es dueño de la ubicacion, asi que
                    esto lista donde tiene stock HOY. El conjunto cambia con cada guardado,
                    picking o ajuste, y por eso el numero se recalcula al abrir y al cambiarlo.
                  -->
                  <v-alert v-if="item.client != null && !disabled" type="info" variant="tonal" density="compact" class="mb-3">
                    <div class="d-flex align-center flex-wrap ga-2">
                      <div class="flex-grow-1">
                        <template v-if="loading_contador">Buscando ubicaciones de {{ item.client.name }}…</template>
                        <template v-else-if="contador == null || contador.total === 0">
                          <b>{{ item.client.name }}</b> no tiene stock en este almacén. Revisá si el almacén es el correcto.
                        </template>
                        <template v-else>
                          <b>{{ item.client.name }}</b> tiene stock en {{ contador.total }} ubicación(es) de este almacén.
                          <span v-if="contador.ya_en_conteo > 0 || contador.en_otro_conteo > 0" class="text-caption d-block">
                            <span v-if="contador.ya_en_conteo > 0">{{ contador.ya_en_conteo }} ya en el conteo</span>
                            <span v-if="contador.ya_en_conteo > 0 && contador.en_otro_conteo > 0"> · </span>
                            <span v-if="contador.en_otro_conteo > 0">{{ contador.en_otro_conteo }} en otro conteo abierto</span>
                            → se pueden agregar {{ contador.disponibles }}.
                          </span>
                        </template>
                      </div>

                      <div v-if="contador != null && contador.total > 0" class="d-flex ga-2">
                        <v-btn size="small" variant="text" @click="verUbicacionesDelCliente">Ver</v-btn>
                        <v-btn size="small" color="info" variant="tonal" :loading="loading_agregar_todas" :disabled="contador.disponibles === 0" @click="prepararAgregarTodas">
                          Agregar {{ contador.disponibles > 0 ? "las " + contador.disponibles : "" }}
                        </v-btn>
                      </div>
                    </div>
                  </v-alert>

                  <v-row align="center">
                    <v-col cols="12" md="4" v-if="!disabled">
                      <v-btn size="small" color="blue-grey" prepend-icon="mdi-plus" class="pl-4 text-white" :disabled="item.almacen == null" @click="dialog_ubicacion = true">
                        <span>Agregar Ubicación</span>
                      </v-btn>
                    </v-col>

                    <v-col cols="12" md="3" class="d-flex justify-center">
                      <v-chip size="small" variant="tonal" color="primary">{{ item.locations.length }} ubicación(es)</v-chip>
                    </v-col>

                    <v-col cols="12" md="5" v-if="item.locations.length > 0">
                      <v-text-field v-model="locations_search" density="compact" label="Buscar en ubicaciones cargadas" prepend-inner-icon="mdi-magnify" clearable hide-details></v-text-field>
                    </v-col>

                    <v-col md="12">
                      <v-alert v-if="item.almacen == null" type="warning" density="compact" variant="tonal" class="mb-2">
                        Elegí primero el almacén del conteo para poder agregar ubicaciones.
                      </v-alert>

                      <v-alert v-else-if="item.locations.length == 0" type="info" density="compact" variant="tonal" class="mb-2">
                        Agregá al menos una ubicación: un conteo sin ubicaciones no se puede emitir.
                      </v-alert>

                      <v-alert v-else-if="filtered_locations.length == 0" type="info" density="compact" variant="tonal" class="mb-2">
                        Ninguna ubicación coincide con la búsqueda.
                      </v-alert>

                      <v-data-table
                        v-else
                        :headers="location_headers"
                        :items="filtered_locations"
                        :items-per-page="10"
                        :items-per-page-options="[5, 10, 25, 50]"
                        density="compact"
                        items-per-page-text="Filas por página"
                      >
                        <template v-slot:item="{ item }">
                          <tr>
                            <td>{{ item.ubicacion.code }}</td>
                            <td>{{ item.ubicacion.zona ? item.ubicacion.zona.name : "-" }}</td>
                            <td>{{ item.ubicacion.rack }}</td>
                            <td>{{ item.ubicacion.col }}</td>
                            <td>{{ item.ubicacion.level }}</td>
                            <td>{{ item.ubicacion.status ? item.ubicacion.status.name : "-" }}</td>
                            <td class="text-center" v-if="!disabled">
                              <v-icon color="red" size="small" @click="removeLocation(item)">mdi-delete</v-icon>
                            </td>
                          </tr>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>

                  <!--
                    El orden real lo sella el backend sobre el conjunto entero al guardar y al
                    emitir: por eso no se muestra un numero de recorrido aca, seria mentira
                    mientras la lista se sigue editando.
                  -->
                  <!-- <p class="text-caption mt-2 mb-0 text-medium-emphasis">
                    El orden de recorrido (zona → rack → columna → nivel) lo calcula el sistema al guardar, sobre el conjunto completo.
                  </p> -->

                  <v-btn v-if="step > 0" @click="prev" class="mt-5" color="blue-grey">Atrás</v-btn>
                </v-col>
              </v-row>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-grey lighten-3" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn v-if="formBtn != '' && step == steps.length - 1" color="green darken-1" text type="submit" :loading="loading">{{ formBtn }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <ModalUbicaciones
      :dialog="dialog_ubicacion"
      :almacen="item.almacen"
      :client="filtrar_por_cliente ? item.client : null"
      :excluded_ids="selected_ubicacion_ids"
      :stock_count_id="item.id > 0 ? item.id : null"
      @close="cerrarSelector"
      @select="addLocations"
    />

    <!--
      El aviso de mercaderia ajena va ACA y no en el step: en el step seria permanente y se
      dejaria de leer. En el dialogo aparece justo antes de la accion, y solo cuando aplica.
    -->
    <DialogYesNo
      :open="dialog_agregar_todas"
      :title="'Agregar ' + por_agregar.length + ' ubicación(es) al conteo'"
      :message="mensaje_agregar_todas"
      :loading="loading_agregar_todas"
      @cancel="dialog_agregar_todas = false"
      @accept="confirmarAgregarTodas"
    />
  </v-layout>
</template>

<script>
import ValidMixin from "@/mixins/ValidMixin.vue";
import { almacenService } from "@/libs/ws/almacenService";
import { clientService } from "@/libs/ws/clientService";
import { conteoService } from "@/libs/ws/conteoService";
import ModalUbicaciones from "./ModalUbicaciones.vue";
import DialogYesNo from "@/components/DialogYesNo.vue";

export default {
  components: { ModalUbicaciones, DialogYesNo },
  mixins: [ValidMixin],
  props: {
    service: Object,
    button_name: String,
    modal_title: String,
    page_title: String,
    formBtn: String,
    dialog: Boolean,
    updateDialog: Function,
    loadList: Function,
    editItem: Object,
  },
  data() {
    return {
      disabled: false,
      loading: false,
      validStep0: false,

      step: 0,
      steps: [
        { title: "Datos", content: "Almacén, tipo, cliente y fecha" },
        { title: "Ubicaciones", content: "Ubicaciones que va a recorrer el operario" },
      ],

      dialog_ubicacion: false,
      locations_search: "",

      //Atajo por cliente
      contador: null,
      loading_contador: false,
      filtrar_por_cliente: false,//el selector arranca sin el filtro salvo que se pida "Ver"
      dialog_agregar_todas: false,
      loading_agregar_todas: false,
      por_agregar: [],
      compartidas_por_agregar: 0,

      almacenes: [],
      clients: [],
      types: [],

      item: {
        id: "",
        almacen: null,
        client: null,
        type: null,
        scheduled_date: null,
        observations: "",
        locations: [],
      },
      defaultItem: null,

    };
  },
  computed: {
    es_alta_inicial() {
      return this.item.type != null && this.item.type.id === conteoService.TYPE_ALTA_INICIAL;
    },
    selected_ubicacion_ids() {
      return this.item.locations.map((l) => l.ubicacion.id);
    },
    /**
     * La columna de accion solo existe si se puede editar: su <td> tambien es condicional, y si
     * el header fuera fijo la tabla quedaria con una columna de mas en modo lectura.
     */
    location_headers() {
      const cols = [
        { title: "Código", key: "code", sortable: false, align: "start" },
        { title: "Zona", key: "zona", sortable: false, align: "start" },
        { title: "Rack", key: "rack", sortable: false, align: "start" },
        { title: "Col.", key: "col", sortable: false, align: "start" },
        { title: "Nivel", key: "level", sortable: false, align: "start" },
        //Estado de la UBICACION (libre / con stock / llena / bloqueada), no del conteo: en
        //borrador la linea del conteo siempre esta pendiente, asi que no aportaria nada.
        { title: "Estado", key: "status", sortable: false, align: "start" },
      ];
      if (!this.disabled) cols.push({ title: "Acción", key: "actions", sortable: false, align: "center" });
      return cols;
    },
    mensaje_agregar_todas() {
      let msg = "Se van a agregar al conteo.";
      if (this.compartidas_por_agregar > 0) {
        //Decision K: la ubicacion entra entera. Es la sorpresa que aparece en la revision.
        msg =
          this.compartidas_por_agregar +
          " de ellas tienen también stock de otros clientes. El conteo cuenta todo lo que haya en cada ubicación, así que ese stock se va a contar y va a aparecer en la revisión.";
      }
      return msg;
    },
    filtered_locations() {
      const q = (this.locations_search || "").trim().toLowerCase();
      if (q === "") return this.item.locations;

      return this.item.locations.filter((l) => {
        const u = l.ubicacion;
        if (u == null) return false;
        const campos = [u.code, u.zona ? u.zona.name : null, u.rack];
        return campos.some((v) => v != null && String(v).toLowerCase().includes(q));
      });
    },
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.disabled = this.formBtn == "";
        this.step = 0;
        this.locations_search = "";
        this.contador = null;
        this.filtrar_por_cliente = false;
        this.por_agregar = [];

        if (this.$refs.form) this.$refs.form.resetValidation();
        if (this.$refs.form_step0) this.$refs.form_step0.resetValidation();

        if (this.editItem != null && this.editItem.id > 0) {
          this.item = JSON.parse(JSON.stringify(this.editItem));
          this.item.locations = [];
          this.loadLocations();
        } else if (this.defaultItem != null) {
          this.item = JSON.parse(JSON.stringify(this.defaultItem));
          //Por defecto el ciclico: el alta inicial es la excepcion, no el caso comun
          this.item.type = this.types.find((t) => t.id === conteoService.TYPE_CICLICO) || null;
          //Se resuelve al ABRIR y no en data(): con la pantalla abierta desde ayer, un default
          //calculado al montar el componente propondria la fecha equivocada.
          this.item.scheduled_date = this.hoy();
        }
      }
    },
  },
  mounted() {
    this.defaultItem = JSON.parse(JSON.stringify(this.item));
    this.loadAlmacenes();
    this.loadClients();
    this.loadTypes();
  },
  methods: {
    /**
     * Hoy en formato YYYY-MM-DD, que es lo que espera un <input type="date">.
     *
     * Se arma con los componentes LOCALES a proposito: toISOString() devuelve UTC, y en
     * Argentina (UTC-3) despues de las 21:00 eso ya es el dia siguiente.
     */
    hoy() {
      const d = new Date();
      const mes = String(d.getMonth() + 1).padStart(2, "0");
      const dia = String(d.getDate()).padStart(2, "0");
      return d.getFullYear() + "-" + mes + "-" + dia;
    },
    loadAlmacenes() {
      almacenService.filter({}).then((response) => {
        if (response.status) this.almacenes = response.result.list;
      });
    },
    loadClients() {
      clientService.filter({}).then((response) => {
        if (response.status) this.clients = response.result.list;
      });
    },
    loadTypes() {
      conteoService.list_types().then((response) => {
        if (response.status) {
          this.types = response.result.list;
          if (this.item.type == null) {
            this.item.type = this.types.find((t) => t.id === conteoService.TYPE_CICLICO) || null;
          }
        }
      });
    },
    /** En edicion las ubicaciones ya existen en el back: se traen para poder editarlas. */
    loadLocations() {
      conteoService.list_ubicaciones({ id: this.item.id, has_pagination: false }).then((response) => {
        if (!response.status) return;
        this.item.locations = response.result.list.map((l) => ({
          id: l.id,
          ubicacion: l.ubicacion,
        }));
        //Recien aca se sabe cuantas de las del cliente YA estan en el conteo
        this.refrescarContador();
      });
    },

    // --- Navegacion entre steps
    async next() {
      if (this.step === 0 && this.$refs.form_step0) {
        const { valid } = await this.$refs.form_step0.validate();
        if (!valid) {
          this.toast.error("Por favor complete todos los campos obligatorios");
          return;
        }
      }
      if (this.step < this.steps.length - 1) this.step++;

      //El contador es del step 2: no tiene sentido pedirlo antes de que se vea
      if (this.step === 1 && this.contador == null) this.refrescarContador();
    },
    prev() {
      if (this.step > 0) this.step--;
    },

    onAlmacenChange(nuevo) {
      //Cambiar de almacen invalida las ubicaciones cargadas: pertenecen a otro deposito
      if (this.item.locations.length === 0) return;

      const nuevo_id = nuevo != null ? nuevo.id : null;
      const mismas = this.item.locations.every((l) => {
        const almacen_ubi = l.ubicacion && l.ubicacion.zona && l.ubicacion.zona.almacen ? l.ubicacion.zona.almacen.id : null;
        return almacen_ubi != null && almacen_ubi === nuevo_id;
      });
      if (!mismas) {
        this.item.locations = [];
        this.toast.info("Se limpiaron las ubicaciones porque pertenecen a otro almacén.");
      }
    },
    // --- Atajo por cliente
    /**
     * Recalcula el contador. Se llama al abrir el modal, al pasar al step 2, al cambiar de
     * cliente y despues de agregar: el conjunto depende del stock, que se mueve solo.
     *
     * Manda las ubicaciones ya cargadas porque al CREAR el conteo todavia no existe en la base:
     * esa lista de trabajo vive nada mas que en la pantalla.
     */
    refrescarContador() {
      if (this.item.client == null || this.item.almacen == null) {
        this.contador = null;
        return;
      }

      this.loading_contador = true;
      conteoService
        .contar_ubicaciones_cliente({
          almacen: { id: this.item.almacen.id },
          client: { id: this.item.client.id },
          ubicaciones_ids: this.selected_ubicacion_ids,
          stock_count: this.item.id > 0 ? { id: this.item.id } : null,
        })
        .then((response) => {
          this.contador = response.status ? response.result : null;
        })
        .catch(() => {
          this.contador = null;
        })
        .finally(() => {
          this.loading_contador = false;
        });
    },
    verUbicacionesDelCliente() {
      this.filtrar_por_cliente = true;
      this.dialog_ubicacion = true;
    },
    cerrarSelector() {
      this.dialog_ubicacion = false;
      this.filtrar_por_cliente = false;
    },
    /**
     * Trae la lista real y arma la confirmacion. El contador da el numero para el boton, pero
     * para agregar hacen falta las Ubicacion en si, y de paso el dato de cuantas comparten.
     */
    prepararAgregarTodas() {
      if (this.item.client == null || this.item.almacen == null) return;

      this.loading_agregar_todas = true;
      conteoService
        .preview_ubicaciones({
          almacen: { id: this.item.almacen.id },
          client: { id: this.item.client.id },
          stock_count: this.item.id > 0 ? { id: this.item.id } : null,
        })
        .then((response) => {
          if (!response.status) {
            this.toast.error(response.msg);
            return;
          }

          const ya_cargadas = this.selected_ubicacion_ids;
          this.por_agregar = response.result.list.filter((u) => u.seleccionable && !ya_cargadas.includes(u.id));
          this.compartidas_por_agregar = this.por_agregar.filter((u) => u.has_other_client_stock).length;

          if (this.por_agregar.length === 0) {
            this.toast.info("No quedan ubicaciones nuevas para agregar.");
            return;
          }
          this.dialog_agregar_todas = true;
        })
        .catch(() => {
          this.toast.error("Error al buscar las ubicaciones del cliente.");
        })
        .finally(() => {
          this.loading_agregar_todas = false;
        });
    },
    confirmarAgregarTodas() {
      this.addLocations(this.por_agregar);
      this.dialog_agregar_todas = false;
      this.por_agregar = [];
      this.compartidas_por_agregar = 0;
    },

    /**
     * Cambiar de cliente NO limpia las ubicaciones cargadas, a diferencia de cambiar de almacen.
     * Con el almacen se limpian porque literalmente pertenecen a otro deposito; el cliente solo
     * acota que se ofrece, y las ya elegidas siguen siendo validas.
     */
    onClientChange() {
      this.contador = null;
      this.refrescarContador();
    },
    addLocations(ubicaciones) {
      const arr = Array.isArray(ubicaciones) ? ubicaciones : [ubicaciones];
      arr.forEach((ubicacion) => {
        if (this.selected_ubicacion_ids.includes(ubicacion.id)) return;
        //id null = todavia no existe en el back; se da de alta al guardar
        this.item.locations.push({ id: null, ubicacion: ubicacion });
      });
      this.cerrarSelector();
      this.refrescarContador();
    },
    removeLocation(location) {
      //Recibo el objeto (no el indice) porque puede venir de filtered_locations,
      //cuyos indices no coinciden con item.locations cuando hay busqueda activa.
      const idx = this.item.locations.indexOf(location);
      if (idx >= 0) this.item.locations.splice(idx, 1);
      this.refrescarContador();
    },

    async createItem() {
      if (this.loading) return;

      /**
       * El sub-form del step 0 vive dentro de un v-if, asi que al confirmar (estando en el
       * step 1) el ref ya no existe: ahi la validacion la hizo next(). Se re-valida solo si
       * esta montado, y aparte se chequean a mano los dos campos sin los que no se puede armar
       * el request.
       */
      if (this.$refs.form_step0) {
        const { valid } = await this.$refs.form_step0.validate();
        if (!valid) {
          this.step = 0;
          this.toast.error("Por favor complete todos los campos obligatorios");
          return;
        }
      }

      if (this.item.almacen == null || this.item.type == null) {
        this.step = 0;
        this.toast.error("El conteo necesita almacén y tipo.");
        return;
      }

      this.loading = true;

      const data = {
        id: this.item.id,
        almacen: { id: this.item.almacen.id },
        type: { id: this.item.type.id },
        client: this.item.client != null ? { id: this.item.client.id } : null,
        scheduled_date: this.item.scheduled_date,
        observations: this.item.observations,
        //editCollection del backend espera <coleccion>_arr, y la coleccion se llama 'locations'.
        //Manda la lista COMPLETA: el back da de alta las nuevas y borra las que ya no vienen.
        locations_arr: this.item.locations.map((l) => ({
          id: l.id,
          ubicacion: { id: l.ubicacion.id },
        })),
      };

      try {
        const es_alta = !(this.item.id > 0);
        const response = es_alta ? await this.service.create(data) : await this.service.update(data);

        if (!response.status) {
          this.toast.error(Object.prototype.hasOwnProperty.call(response, "message") ? response.message : response.msg);
          return;
        }

        this.toast.success("El " + this.modal_title + (es_alta ? " se creó" : " se modificó") + " correctamente.");
        this.$emit("updateDialog", false);
        this.$emit("loadList");
      } catch (e) {
        this.toast.error("Error al guardar el conteo.");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
